import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import DOMPurify from 'dompurify';

const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li', 'a', 'h2', 'h3', 'code', 'pre', 'blockquote', 'img'];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'src', 'alt'];

/** True when the HTML has no visible text/content (empty editor output like "<p></p>"). */
export function isRichTextEmpty(html?: string | null): boolean {
  if (!html) return true;
  const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return text.length === 0;
}

/** Flattens rich-text HTML to a plain-text string — for compact previews/excerpts
 *  where rendered markup (and raw tags) would be noise. */
export function richTextToPlain(html?: string | null): string {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Renders trainer/admin-authored rich text safely (sanitized HTML). */
export function RichText({ html, className }: { html: string; className?: string }) {
  const clean = useMemo(
    () => DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, ADD_ATTR: ['target'] }),
    [html],
  );
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [lightbox]);

  // Diagrams embedded in lesson content (architecture diagrams, flowcharts) are
  // otherwise stuck at the description's fixed width — this is the static-image
  // counterpart to the visualizer Player's fullscreen toggle, so any diagram
  // anywhere in the platform gets the same "view it properly" affordance.
  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const img = target as HTMLImageElement;
      setLightbox({ src: img.src, alt: img.alt });
    }
  };

  return (
    <>
      <div className={`kp-rich ${className ?? ''}`} onClick={onClick} dangerouslySetInnerHTML={{ __html: clean }} />
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.alt || 'Enlarged image'}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close"
            className="absolute right-5 top-5 flex size-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <X className="size-5" />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-h-full max-w-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
